import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import type { MeetingSeries, MeetingSeriesCreatePayload, UUID } from "@/lib/types";

export function useMeetingSeries() {
  return useQuery({ queryKey: queryKeys.meetingSeries, queryFn: () => api.listMeetingSeries() });
}

export function useSeries(seriesId: UUID | undefined) {
  return useQuery({
    queryKey: seriesId ? queryKeys.series(seriesId) : ["meeting-series", "none"],
    queryFn: () => api.getSeries(seriesId as UUID),
    enabled: !!seriesId,
  });
}

export function useCreateSeries() {
  const qc = useQueryClient();
  return useMutation<MeetingSeries, Error, MeetingSeriesCreatePayload>({
    mutationFn: (payload) => api.createMeetingSeries(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.meetingSeries });
    },
  });
}

export function useUpdateSeries(seriesId: UUID) {
  const qc = useQueryClient();
  return useMutation<MeetingSeries, Error, Partial<MeetingSeriesCreatePayload>>({
    mutationFn: (payload) => api.updateSeries(seriesId, payload),
    onSuccess: (series) => {
      qc.setQueryData(queryKeys.series(seriesId), series);
      void qc.invalidateQueries({ queryKey: queryKeys.meetingSeries });
    },
  });
}

export function useDeleteSeries(seriesId: UUID) {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => api.deleteSeries(seriesId),
    onSuccess: () => {
      qc.removeQueries({ queryKey: queryKeys.series(seriesId) });
      void qc.invalidateQueries({ queryKey: queryKeys.meetingSeries });
    },
  });
}
